import React, { useState, useEffect } from 'react';
import PageContainer from '../../components/common/PageContainer';
import PageSection from '../../components/common/PageSection';
import AdminNavigation from '../../components/admin/AdminNavigation';
import { useAuth } from '../../context/shared/AuthContext';
import apiService from '../../services/shared/apiService';

interface GiftAnalytics {
  totalRequests: number;
  approvedRequests: number;
  deniedRequests: number;
  pendingRequests: number;
  totalTokensAwarded: number;
  averageTokensPerRequest: number;
  requestsByTier: {
    'High Need': number;
    'Medium Need': number;
    'Low Need': number;
    'Ineligible': number;
  };
  requestsByIncome: {
    'under_25k': number;
    '25k_50k': number;
    '50k_75k': number;
    '75k_100k': number;
    'over_100k': number;
    'prefer_not_to_say': number;
  };
  requestsByHardship: {
    'job_loss': number;
    'mental_health_strain': number;
    'medical_expenses': number;
    'family_care': number;
    'none': number;
  };
  dailyRequests: Array<{
    date: string;
    count: number;
    tokensAwarded: number;
  }>;
  escalationRate: number;
  appealRate: number;
}

const GiftAnalyticsDashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<GiftAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAnalytics();
    }
  }, [user, timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGiftAnalytics();
      setAnalytics(response);
    } catch (err: any) {
      setError('Failed to fetch analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatPercentage = (num: number) => {
    return `${(num * 100).toFixed(1)}%`;
  };

  if (!user || user.role !== 'admin') {
    return (
      <PageContainer>
        <PageSection title="Access Denied" emoji="🚫">
          <p>You must be an authorized administrator to view this page.</p>
        </PageSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <AdminNavigation />
      <PageSection title="Gift System Analytics" emoji="📊">
        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Time Range Selector */}
          <div className="flex justify-end">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300  rounded-lg bg-white  text-gray-900 "
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p>Loading analytics data...</p>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100  rounded-lg">
                      <span className="text-blue-600  text-xl">📝</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 ">Total Requests</p>
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(analytics.totalRequests)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100  rounded-lg">
                      <span className="text-green-600  text-xl">✅</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 ">Approved</p>
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(analytics.approvedRequests)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100  rounded-lg">
                      <span className="text-yellow-600  text-xl">🎁</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 ">Tokens Awarded</p>
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(analytics.totalTokensAwarded)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100  rounded-lg">
                      <span className="text-purple-600  text-xl">📈</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 ">Avg per Request</p>
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(analytics.averageTokensPerRequest)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Rate */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">Approval Rate</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600 ">
                      {formatPercentage(analytics.approvedRequests / analytics.totalRequests)}
                    </p>
                    <p className="text-sm text-gray-600 ">Approval Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600 ">
                      {formatPercentage(analytics.deniedRequests / analytics.totalRequests)}
                    </p>
                    <p className="text-sm text-gray-600 ">Denial Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-600 ">
                      {formatPercentage(analytics.pendingRequests / analytics.totalRequests)}
                    </p>
                    <p className="text-sm text-gray-600 ">Pending Rate</p>
                  </div>
                </div>
              </div>

              {/* Requests by Tier */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">Requests by Need Tier</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(analytics.requestsByTier).map(([tier, count]) => (
                    <div key={tier} className="text-center">
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(count)}
                      </p>
                      <p className="text-sm text-gray-600 ">{tier}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Requests by Income */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">Requests by Income Range</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(analytics.requestsByIncome).map(([income, count]) => (
                    <div key={income} className="text-center">
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(count)}
                      </p>
                      <p className="text-sm text-gray-600 ">
                        {income.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Requests by Hardship */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">Requests by Hardship Type</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(analytics.requestsByHardship).map(([hardship, count]) => (
                    <div key={hardship} className="text-center">
                      <p className="text-2xl font-bold text-gray-900 ">
                        {formatNumber(count)}
                      </p>
                      <p className="text-sm text-gray-600 ">
                        {hardship.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* System Health Metrics */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">System Health Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-orange-600 ">
                      {formatPercentage(analytics.escalationRate)}
                    </p>
                    <p className="text-sm text-gray-600 ">Escalation Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600 ">
                      {formatPercentage(analytics.appealRate)}
                    </p>
                    <p className="text-sm text-gray-600 ">Appeal Rate</p>
                  </div>
                </div>
              </div>

              {/* Daily Trends */}
              <div className="bg-white  p-6 rounded-lg border border-gray-200 ">
                <h3 className="text-lg font-medium mb-4">Daily Request Trends</h3>
                <div className="space-y-2">
                  {analytics.dailyRequests.slice(-7).map((day) => (
                    <div key={day.date} className="flex justify-between items-center p-2 bg-gray-50  rounded">
                      <span className="text-sm text-gray-600 ">
                        {new Date(day.date).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-4">
                        <span className="text-sm text-gray-900 ">
                          {day.count} requests
                        </span>
                        <span className="text-sm text-green-600 ">
                          {formatNumber(day.tokensAwarded)} tokens
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 ">No analytics data available.</p>
            </div>
          )}
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default GiftAnalyticsDashboard; 